import type { SectionId } from "./organizations";

// The document type catalogue, taxonomy L1-v3 (`docs/document-types.md`): the 89 types an Intake document is filed as,
// in 23 units of four domains. A type's id names its domain and its unit — `fin.ap.vendor_invoice` is a type of
// payables (`fin.ap`), in Finance (`fin`) — and never changes: renaming a type changes its label, never its id, so a
// filing that names an id goes on meaning the same type. Only ids, labels and what the catalogue marks a type with are
// here, not what makes a document one type rather than its neighbour — a type's rule and what it excludes — since the
// frontend bundles this file. The API and the frontend share these.

/** The four domains, in the catalogue's order. */
export const DOCUMENT_DOMAIN_IDS = ["fin", "ppl", "lgl", "corp"] as const;
export type DocumentDomainId = (typeof DOCUMENT_DOMAIN_IDS)[number];

/** A domain: what it is called, and the section of the app whose colour it is shown in. */
export interface DocumentDomain {
	readonly label: string;
	readonly section: SectionId;
}

export const DOCUMENT_DOMAINS: Readonly<Record<DocumentDomainId, DocumentDomain>> = {
	fin: { label: "Finance", section: "finance" },
	ppl: { label: "People", section: "people" },
	lgl: { label: "Legal", section: "legal" },
	corp: { label: "Corporate", section: "equity" },
};

/** The 23 units, each `<domain>.<unit>`, in the catalogue's order. */
export const DOCUMENT_UNIT_IDS = [
	"fin.ap",
	"fin.ar",
	"fin.bank",
	"fin.pay",
	"fin.tax",
	"fin.trs",
	"fin.grant",
	"fin.rep",
	"ppl.hire",
	"ppl.agr",
	"ppl.life",
	"ppl.time",
	"ppl.stat",
	"lgl.com",
	"lgl.prot",
	"lgl.pol",
	"lgl.disp",
	"lgl.ins",
	"corp.form",
	"corp.eq",
	"corp.gov",
	"corp.inv",
	"corp.regs",
] as const;
export type DocumentUnitId = (typeof DOCUMENT_UNIT_IDS)[number];

/** What each unit is called: its heading in the catalogue, without the catalogue's own notes. */
export const DOCUMENT_UNIT_LABELS: Readonly<Record<DocumentUnitId, string>> = {
	"fin.ap": "Payables",
	"fin.ar": "Receivables",
	"fin.bank": "Banking",
	"fin.pay": "Payroll",
	"fin.tax": "Tax",
	"fin.trs": "Treasury",
	"fin.grant": "Grants",
	"fin.rep": "Financials",
	"ppl.hire": "Hiring",
	"ppl.agr": "Worker agreements",
	"ppl.life": "Lifecycle",
	"ppl.time": "Time",
	"ppl.stat": "Worker statutory",
	"lgl.com": "Commercial contracts",
	"lgl.prot": "Protective",
	"lgl.pol": "Policies & compliance",
	"lgl.disp": "Disputes",
	"lgl.ins": "Insurance",
	"corp.form": "Formation & registry",
	"corp.eq": "Ownership / equity",
	"corp.gov": "Governance",
	"corp.inv": "Investor",
	"corp.regs": "Registers",
};

/** The 89 types, each `<domain>.<unit>.<leaf>`, in the catalogue's order. */
export const DOCUMENT_TYPE_IDS = [
	"fin.ap.vendor_invoice",
	"fin.ap.vendor_credit_note",
	"fin.ap.expense_receipt",
	"fin.ap.reimbursement_claim",
	"fin.ap.purchase_order",
	"fin.ar.customer_invoice",
	"fin.ar.customer_credit_note",
	"fin.ar.remittance",
	"fin.ar.customer_po",
	"fin.bank.statement",
	"fin.bank.payment_confirmation",
	"fin.bank.mandate",
	"fin.pay.register",
	"fin.pay.payslip",
	"fin.pay.tax_payment",
	"fin.tax.return",
	"fin.tax.assessment",
	"fin.tax.registration",
	"fin.tax.election",
	"fin.tax.counterparty_form",
	"fin.trs.loan",
	"fin.trs.interest_statement",
	"fin.grant.agreement",
	"fin.grant.claim",
	"fin.rep.statement",
	"fin.rep.management_accounts",
	"fin.rep.budget",
	"fin.rep.audit_report",
	"ppl.hire.offer",
	"ppl.agr.employment",
	"ppl.agr.contractor",
	"ppl.agr.ip_assignment",
	"ppl.agr.advisor",
	"ppl.life.role_change",
	"ppl.life.benefits",
	"ppl.life.review",
	"ppl.life.disciplinary",
	"ppl.life.termination",
	"ppl.life.separation",
	"ppl.time.timesheet",
	"ppl.stat.tax_form",
	"ppl.stat.work_authorization",
	"lgl.com.master",
	"lgl.com.order",
	"lgl.com.agreement",
	"lgl.com.lease",
	"lgl.prot.nda",
	"lgl.prot.dpa",
	"lgl.prot.ip_license",
	"lgl.prot.ip_registration",
	"lgl.pol.internal_policy",
	"lgl.pol.terms",
	"lgl.pol.privacy",
	"lgl.pol.permit",
	"lgl.pol.security_report",
	"lgl.pol.gov_notice",
	"lgl.disp.litigation",
	"lgl.disp.demand",
	"lgl.disp.settlement",
	"lgl.ins.policy",
	"lgl.ins.claim",
	"lgl.ins.certificate",
	"corp.form.incorporation",
	"corp.form.bylaws",
	"corp.form.registry_filing",
	"corp.form.registry_extract",
	"corp.eq.cap_table",
	"corp.eq.issuance",
	"corp.eq.convertible",
	"corp.eq.spa",
	"corp.eq.option_grant",
	"corp.eq.transaction",
	"corp.eq.valuation",
	"corp.eq.incentive_plan",
	"corp.eq.warrant",
	"corp.gov.minutes",
	"corp.gov.board_consent",
	"corp.gov.shareholder_consent",
	"corp.gov.board_pack",
	"corp.gov.director_change",
	"corp.gov.indemnification",
	"corp.inv.round_agreement",
	"corp.inv.sha",
	"corp.inv.side_letter",
	"corp.inv.term_sheet",
	"corp.inv.update",
	"corp.regs.register",
	"corp.regs.ubo",
	"corp.regs.identity",
] as const;
export type DocumentTypeId = (typeof DOCUMENT_TYPE_IDS)[number];

/**
 * What a document of a type is to the fact it records (the catalogue's tag): `agreed` — the document is the act itself,
 * so the executed document is authoritative, always; `observed` — it reports an act or a state recorded elsewhere, and
 * is authoritative only until a tool is the declared source; `either` — the country pack decides, form by form.
 */
export const DOCUMENT_TYPE_TAGS = ["agreed", "observed", "either"] as const;
export type DocumentTypeTag = (typeof DOCUMENT_TYPE_TAGS)[number];

/** A type: what it is called, and what the catalogue marks it with. */
export interface DocumentType {
	readonly label: string;
	readonly tag: DocumentTypeTag;
	/** A rendered report of data a tool holds, which corroborates once that tool is the declared source. */
	readonly snapshot: boolean;
	/** The country pack gives the type its local name: a W-2, a 409A, a cégkivonat. */
	readonly packNamed: boolean;
}

export const DOCUMENT_TYPES: Readonly<Record<DocumentTypeId, DocumentType>> = {
	"fin.ap.vendor_invoice": { label: "Vendor invoice", tag: "agreed", snapshot: false, packNamed: false },
	"fin.ap.vendor_credit_note": { label: "Vendor credit note", tag: "agreed", snapshot: false, packNamed: false },
	"fin.ap.expense_receipt": { label: "Expense receipt", tag: "observed", snapshot: false, packNamed: false },
	"fin.ap.reimbursement_claim": { label: "Reimbursement claim", tag: "agreed", snapshot: false, packNamed: false },
	"fin.ap.purchase_order": { label: "Purchase order", tag: "agreed", snapshot: false, packNamed: false },
	"fin.ar.customer_invoice": { label: "Customer invoice", tag: "agreed", snapshot: false, packNamed: false },
	"fin.ar.customer_credit_note": { label: "Customer credit note", tag: "agreed", snapshot: false, packNamed: false },
	"fin.ar.remittance": { label: "Remittance / receipt", tag: "observed", snapshot: false, packNamed: false },
	"fin.ar.customer_po": { label: "Customer purchase order", tag: "agreed", snapshot: false, packNamed: false },
	"fin.bank.statement": { label: "Bank statement", tag: "observed", snapshot: false, packNamed: false },
	"fin.bank.payment_confirmation": {
		label: "Payment confirmation",
		tag: "observed",
		snapshot: false,
		packNamed: false,
	},
	"fin.bank.mandate": { label: "Bank mandate / details", tag: "agreed", snapshot: false, packNamed: false },
	"fin.pay.register": { label: "Payroll register", tag: "observed", snapshot: true, packNamed: false },
	"fin.pay.payslip": { label: "Payslip", tag: "observed", snapshot: false, packNamed: false },
	"fin.pay.tax_payment": { label: "Payroll tax payment", tag: "observed", snapshot: false, packNamed: false },
	"fin.tax.return": { label: "Tax return", tag: "agreed", snapshot: false, packNamed: true },
	"fin.tax.assessment": { label: "Tax assessment / notice", tag: "agreed", snapshot: false, packNamed: false },
	"fin.tax.registration": { label: "Tax registration", tag: "agreed", snapshot: false, packNamed: true },
	"fin.tax.election": { label: "Tax election", tag: "agreed", snapshot: false, packNamed: true },
	"fin.tax.counterparty_form": { label: "Counterparty tax form", tag: "agreed", snapshot: false, packNamed: true },
	"fin.trs.loan": { label: "Loan / facility agreement", tag: "agreed", snapshot: false, packNamed: false },
	"fin.trs.interest_statement": { label: "Interest statement", tag: "observed", snapshot: false, packNamed: false },
	"fin.grant.agreement": { label: "Grant agreement", tag: "agreed", snapshot: false, packNamed: false },
	"fin.grant.claim": { label: "Grant claim / report", tag: "observed", snapshot: false, packNamed: false },
	"fin.rep.statement": { label: "Financial statement", tag: "observed", snapshot: true, packNamed: false },
	"fin.rep.management_accounts": { label: "Management accounts", tag: "observed", snapshot: true, packNamed: false },
	"fin.rep.budget": { label: "Budget / forecast", tag: "agreed", snapshot: false, packNamed: false },
	"fin.rep.audit_report": { label: "Audit report", tag: "agreed", snapshot: false, packNamed: false },
	"ppl.hire.offer": { label: "Job offer / offer letter", tag: "agreed", snapshot: false, packNamed: false },
	"ppl.agr.employment": { label: "Employment contract", tag: "agreed", snapshot: false, packNamed: false },
	"ppl.agr.contractor": { label: "Contractor agreement", tag: "agreed", snapshot: false, packNamed: false },
	"ppl.agr.ip_assignment": { label: "IP / invention assignment", tag: "agreed", snapshot: false, packNamed: false },
	"ppl.agr.advisor": { label: "Advisor agreement", tag: "agreed", snapshot: false, packNamed: false },
	"ppl.life.role_change": { label: "Role / title change", tag: "agreed", snapshot: false, packNamed: false },
	"ppl.life.benefits": { label: "Benefits enrollment", tag: "agreed", snapshot: false, packNamed: false },
	"ppl.life.review": { label: "Performance review", tag: "agreed", snapshot: false, packNamed: false },
	"ppl.life.disciplinary": { label: "PIP / disciplinary", tag: "agreed", snapshot: false, packNamed: false },
	"ppl.life.termination": {
		label: "Termination / offboarding letter",
		tag: "agreed",
		snapshot: false,
		packNamed: false,
	},
	"ppl.life.separation": {
		label: "Separation / release agreement",
		tag: "agreed",
		snapshot: false,
		packNamed: false,
	},
	"ppl.time.timesheet": { label: "Timesheet", tag: "observed", snapshot: false, packNamed: false },
	"ppl.stat.tax_form": { label: "Worker tax form", tag: "either", snapshot: false, packNamed: true },
	"ppl.stat.work_authorization": {
		label: "Right-to-work evidence",
		tag: "observed",
		snapshot: false,
		packNamed: true,
	},
	"lgl.com.master": { label: "Master agreement", tag: "agreed", snapshot: false, packNamed: false },
	"lgl.com.order": { label: "Order form / SOW", tag: "agreed", snapshot: false, packNamed: false },
	"lgl.com.agreement": { label: "Commercial agreement", tag: "agreed", snapshot: false, packNamed: false },
	"lgl.com.lease": { label: "Lease", tag: "agreed", snapshot: false, packNamed: false },
	"lgl.prot.nda": { label: "NDA", tag: "agreed", snapshot: false, packNamed: false },
	"lgl.prot.dpa": { label: "DPA / data-processing", tag: "agreed", snapshot: false, packNamed: false },
	"lgl.prot.ip_license": { label: "IP license / assignment", tag: "agreed", snapshot: false, packNamed: false },
	"lgl.prot.ip_registration": { label: "IP registration", tag: "observed", snapshot: false, packNamed: true },
	"lgl.pol.internal_policy": { label: "Internal policy", tag: "agreed", snapshot: false, packNamed: false },
	"lgl.pol.terms": { label: "Terms of service", tag: "agreed", snapshot: false, packNamed: false },
	"lgl.pol.privacy": { label: "Privacy policy", tag: "agreed", snapshot: false, packNamed: false },
	"lgl.pol.permit": { label: "Regulatory license / permit", tag: "agreed", snapshot: false, packNamed: true },
	"lgl.pol.security_report": {
		label: "Security / compliance report",
		tag: "observed",
		snapshot: false,
		packNamed: false,
	},
	"lgl.pol.gov_notice": { label: "Government notice", tag: "agreed", snapshot: false, packNamed: false },
	"lgl.disp.litigation": { label: "Litigation / dispute record", tag: "observed", snapshot: false, packNamed: false },
	"lgl.disp.demand": { label: "Demand letter", tag: "agreed", snapshot: false, packNamed: false },
	"lgl.disp.settlement": { label: "Settlement", tag: "agreed", snapshot: false, packNamed: false },
	"lgl.ins.policy": { label: "Insurance policy", tag: "agreed", snapshot: false, packNamed: false },
	"lgl.ins.claim": { label: "Insurance claim", tag: "observed", snapshot: false, packNamed: false },
	"lgl.ins.certificate": { label: "Certificate of insurance", tag: "observed", snapshot: false, packNamed: false },
	"corp.form.incorporation": {
		label: "Incorporation certificate",
		tag: "observed",
		snapshot: false,
		packNamed: false,
	},
	"corp.form.bylaws": { label: "Bylaws / articles", tag: "agreed", snapshot: false, packNamed: true },
	"corp.form.registry_filing": { label: "Registry filing", tag: "agreed", snapshot: false, packNamed: true },
	"corp.form.registry_extract": {
		label: "Registry extract / good standing",
		tag: "observed",
		snapshot: false,
		packNamed: true,
	},
	"corp.eq.cap_table": { label: "Cap table", tag: "observed", snapshot: true, packNamed: false },
	"corp.eq.issuance": { label: "Share issuance / certificate", tag: "agreed", snapshot: false, packNamed: false },
	"corp.eq.convertible": { label: "Convertible instrument", tag: "agreed", snapshot: false, packNamed: true },
	"corp.eq.spa": { label: "Stock purchase agreement", tag: "agreed", snapshot: false, packNamed: false },
	"corp.eq.option_grant": { label: "Option grant", tag: "agreed", snapshot: false, packNamed: false },
	"corp.eq.transaction": { label: "Equity transaction", tag: "agreed", snapshot: false, packNamed: false },
	"corp.eq.valuation": { label: "Share valuation", tag: "agreed", snapshot: false, packNamed: true },
	"corp.eq.incentive_plan": { label: "Equity incentive plan", tag: "agreed", snapshot: false, packNamed: false },
	"corp.eq.warrant": { label: "Warrant", tag: "agreed", snapshot: false, packNamed: false },
	"corp.gov.minutes": { label: "Board minutes", tag: "agreed", snapshot: false, packNamed: false },
	"corp.gov.board_consent": { label: "Board consent / resolution", tag: "agreed", snapshot: false, packNamed: false },
	"corp.gov.shareholder_consent": { label: "Shareholder consent", tag: "agreed", snapshot: false, packNamed: false },
	"corp.gov.board_pack": { label: "Board pack", tag: "observed", snapshot: false, packNamed: false },
	"corp.gov.director_change": {
		label: "Director appointment / resignation",
		tag: "agreed",
		snapshot: false,
		packNamed: false,
	},
	"corp.gov.indemnification": {
		label: "D&O indemnification agreement",
		tag: "agreed",
		snapshot: false,
		packNamed: false,
	},
	"corp.inv.round_agreement": {
		label: "Investment agreement (round)",
		tag: "agreed",
		snapshot: false,
		packNamed: true,
	},
	"corp.inv.sha": { label: "SHA / voting / ROFR", tag: "agreed", snapshot: false, packNamed: true },
	"corp.inv.side_letter": { label: "Side letter", tag: "agreed", snapshot: false, packNamed: false },
	"corp.inv.term_sheet": { label: "Term sheet", tag: "agreed", snapshot: false, packNamed: false },
	"corp.inv.update": { label: "Investor update", tag: "observed", snapshot: false, packNamed: false },
	"corp.regs.register": { label: "Statutory register", tag: "observed", snapshot: true, packNamed: true },
	"corp.regs.ubo": { label: "UBO / beneficial ownership", tag: "agreed", snapshot: false, packNamed: true },
	"corp.regs.identity": { label: "Identity document", tag: "observed", snapshot: false, packNamed: false },
};

const UNIT_IDS: ReadonlySet<string> = new Set(DOCUMENT_UNIT_IDS);
const DOMAIN_IDS: ReadonlySet<string> = new Set(DOCUMENT_DOMAIN_IDS);

/** Whether an id is one of the catalogue's units. */
export function isDocumentUnitId(id: string): id is DocumentUnitId {
	return UNIT_IDS.has(id);
}

/** Whether an id is one of the catalogue's domains. */
export function isDocumentDomainId(id: string): id is DocumentDomainId {
	return DOMAIN_IDS.has(id);
}

/** The unit a type is of: `fin.ap.vendor_invoice` is a type of payables, `fin.ap`. */
export function documentUnitOf(type: DocumentTypeId): DocumentUnitId {
	const unit = type.slice(0, type.lastIndexOf("."));
	if (!isDocumentUnitId(unit)) {
		throw new Error(`The document type "${type}" is of no unit of the catalogue`);
	}
	return unit;
}

/** The domain a unit or a type is in: `fin.ap` and `fin.ap.vendor_invoice` are both in Finance, `fin`. */
export function documentDomainOf(id: DocumentUnitId | DocumentTypeId): DocumentDomainId {
	const domain = id.slice(0, id.indexOf("."));
	if (!isDocumentDomainId(domain)) {
		throw new Error(`"${id}" is in no domain of the catalogue`);
	}
	return domain;
}

/** The units of a domain, in the catalogue's order. */
export function documentUnitsIn(domain: DocumentDomainId): DocumentUnitId[] {
	return DOCUMENT_UNIT_IDS.filter(unit => documentDomainOf(unit) === domain);
}

/** The types of a unit, in the catalogue's order. */
export function documentTypesIn(unit: DocumentUnitId): DocumentTypeId[] {
	return DOCUMENT_TYPE_IDS.filter(type => documentUnitOf(type) === unit);
}

/** What a type is called: "Vendor invoice". */
export function documentTypeLabel(type: DocumentTypeId): string {
	return DOCUMENT_TYPES[type].label;
}

/** Where a type sits in the catalogue, in words: "Finance › Payables › Vendor invoice". */
export function documentTypePath(type: DocumentTypeId): string {
	const unit = documentUnitOf(type);
	return [DOCUMENT_DOMAINS[documentDomainOf(type)].label, DOCUMENT_UNIT_LABELS[unit], documentTypeLabel(type)].join(
		" › ",
	);
}
